/**
 * Reset Password Page
 * 
 * Page for resetting password using token from email link.
 * 
 * URL: /reset-password?token=abc
 * 
 * @module pages/auth
 */

import { useState, useEffect } from 'react';
import { TenantLogo } from '@butter/ui/components/data-display';
import { EyeIcon } from '@butter/ui/icons';
import { PasswordStrengthIndicator } from './components';
import { validateUserPassword, validatePasswordMatch } from '~/lib/utils/passwordValidation';
import { authService, useAuth, AccountSetupRequiredError, useAccountSetupRedirect } from '~/services/auth';
import { QueryParams } from '~/lib/queryParams';
import { useTenantNavigate, useTenantSearchParams } from '~/lib/navigation';
import { captureException } from '@butter/monitoring';

/**
 * `setup-required` is deliberately distinct from `success`, not a flavour of it.
 *
 * The reset SUCCEEDED in both cases — but `success` is watched by the auto-redirect effect
 * below, which sends the member home after 3s. A setup-required member is simultaneously
 * being taken to `/account-setup` by a 5s countdown toast, so sharing the status made the
 * two race: home at 3s, then yanked to setup at 2s later, and "Stay here" stranded them on
 * home with no route to setup at all.
 *
 * ⚠️ No exhaustive `switch` reads this union, so TypeScript will not point you at the
 * places a new member needs handling. There are exactly two: the redirect effect and the
 * success-card early return.
 */
type PageStatus = 'idle' | 'loading' | 'success' | 'setup-required' | 'error';

/**
 * Reset Password Page
 * 
 * Flow:
 * 1. Extract token from URL
 * 2. Show password reset form
 * 3. On submit, call authService.resetPassword()
 * 4. Show success message and redirect to booking
 */
export function ResetPasswordPage() {
  const [searchParams] = useTenantSearchParams();
  const navigate = useTenantNavigate();
  const { login } = useAuth();
  const redirectToAccountSetup = useAccountSetupRedirect();

  const token = searchParams.get(QueryParams.TOKEN) || '';

  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Page state - only track status for form submission, not for token validation
  const [formStatus, setFormStatus] = useState<PageStatus>('idle');
  const [formError, setFormError] = useState<string | null>(null);

  // Derive status from token presence and form status
  // If no token, treat as error state; otherwise use form status
  const hasToken = Boolean(token);
  const status = !hasToken ? 'error' : formStatus;
  const error = !hasToken 
    ? 'Invalid or missing reset token. Please request a new password reset link.'
    : formError;

  // Auto-redirect after success
  useEffect(() => {
    if (status === 'success') {
      // useTenantNavigate carries tenant + club only — the reset token stays behind.
      const timer = setTimeout(() => navigate('/'), 3000);

      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validate password
    const passwordValidation = validateUserPassword(password);
    if (!passwordValidation.isValid) {
      setFormError(passwordValidation.errors[0]);
      return;
    }

    // Validate password match
    const matchError = validatePasswordMatch(password, confirmPassword);
    if (matchError) {
      setFormError(matchError);
      return;
    }

    setFormStatus('loading');

    try {
      const response = await authService.resetPassword(token, password);
      
      // Automatically sign in the user with their new credentials
      if (response.email) {
        console.log('[ResetPasswordPage] Auto-login after password reset');
        await login(response.email, password);
      }
      
      setFormStatus('success');
    } catch (err) {
      // The reset itself SUCCEEDED — this member simply cannot hold a session yet,
      // because their account was never finished (`pending` / `incomplete`). Login
      // refuses them by design. Reporting that as "Failed to reset password" would be
      // false, would tell them to request another link they don't need, and would page
      // someone via Sentry for a working flow. Report success and route them to setup.
      if (err instanceof AccountSetupRequiredError) {
        // NOT 'success' — that status is watched by the 3s home-redirect effect, which
        // would beat the 5s setup countdown and land them somewhere they can't act.
        setFormStatus('setup-required');
        redirectToAccountSetup(err);
        return;
      }

      captureException(err instanceof Error ? err : new Error(String(err)), {
        level: 'error',
        tags: {
          component: 'reset-password',
          operation: 'password-reset',
        },
        extra: {
          hasToken: Boolean(token),
        },
      });
      setFormStatus('error');
      setFormError('Failed to reset password. Please try again or request a new reset link.');
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/');
  };

  // Success state — shared by both outcomes, because the reset itself succeeded in both.
  // Only the closing line differs: where we are actually taking them.
  if (status === 'success' || status === 'setup-required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            {/* Success Icon */}
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-fg-primary mb-2">
              Password Reset Successful
            </h2>
            <p className="text-fg-quaternary mb-4">
              Your password has been updated successfully.
            </p>
            <p className="text-sm text-fg-quaternary">
              {status === 'setup-required'
                ? 'Taking you to account setup…'
                : 'Redirecting you to the home page...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main form view
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <TenantLogo />
          </div>

          {/* Header */}
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-fg-primary">
              Reset Your Password
            </h1>
            <p className="text-fg-quaternary mt-2">
              Enter your new password below.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
              role="alert"
            >
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New Password */}
            <div className="text-sm">
              <label
                htmlFor="password"
                className="block font-medium text-fg-quaternary mb-2"
              >
                New Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={status === 'loading' || !token}
                  required
                  minLength={8}
                  placeholder="Enter new password (min. 8 characters)"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon
                    isOpen={showPassword}
                    className="size-6 text-gray-500"
                    ariaHidden={true}
                  />
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
            </div>

            {/* Confirm Password */}
            <div className="text-sm">
              <label
                htmlFor="confirmPassword"
                className="block font-medium text-fg-quaternary mb-2"
              >
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={status === 'loading' || !token}
                  required
                  minLength={8}
                  placeholder="Confirm new password"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon
                    isOpen={showConfirmPassword}
                    className="size-6 text-gray-500"
                    ariaHidden={true}
                  />
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-red-600">
                  Passwords do not match
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={status === 'loading' || !token}
                className="w-full py-3 px-4 bg-theme-600 text-white font-medium rounded-lg hover:bg-theme-700 focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {status === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </div>
          </form>

          {/* Back Link */}
          <div className="mt-6 text-center">
            <button
              onClick={handleBack}
              className="text-sm text-theme-600 hover:text-theme-700"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
